import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FaBullhorn } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import { fetchProjectsById } from "../../api/customer.api";
import { getProjectIdFromPath } from "../../config/routePermissions";

const FALLBACK_USAGE = {
  planName: null,
  limit: 1000,
  unlimited: false,
  sentToday: 0,
  remaining: 1000,
  blocked: false,
};

function resolveProjectId(paramId, pathname) {
  return String(paramId || getProjectIdFromPath(pathname) || "").trim();
}

function pickUsage(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.dailyCampaignUsage) return payload.dailyCampaignUsage;
  if (payload.project?.dailyCampaignUsage) return payload.project.dailyCampaignUsage;
  if (payload.data?.dailyCampaignUsage) return payload.data.dailyCampaignUsage;
  if (payload.data?.project?.dailyCampaignUsage) {
    return payload.data.project.dailyCampaignUsage;
  }
  return null;
}

const DailyCampaignLimitBanner = ({
  title = "Campaign Report",
  icon = <FaBullhorn />,
}) => {
  const { projectId: paramProjectId } = useParams();
  const location = useLocation();
  const projectId = resolveProjectId(paramProjectId, location.pathname);
  const [usage, setUsage] = useState(FALLBACK_USAGE);
  const [loading, setLoading] = useState(Boolean(projectId));

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setUsage(FALLBACK_USAGE);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const load = async () => {
      try {
        const dedicated = await apihubGet(
          `/getInteractedCustomerDetail/dailycampaignusage/${encodeURIComponent(projectId)}`,
        );
        const fromDedicated = pickUsage(dedicated);
        if (fromDedicated) {
          if (!cancelled) {
            setUsage(fromDedicated);
            setLoading(false);
          }
          return;
        }
      } catch {
        // Fall through to project payload.
      }

      try {
        const res = await fetchProjectsById({ id: projectId });
        const fromProject = pickUsage(res);
        if (!cancelled) {
          setUsage(fromProject || FALLBACK_USAGE);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setUsage(FALLBACK_USAGE);
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const sentToday = Number(usage?.sentToday) || 0;
  const unlimited = Boolean(usage?.unlimited);
  const limit = unlimited ? null : Number(usage?.limit) || 0;
  const remaining = unlimited
    ? null
    : usage?.remaining == null
      ? Math.max(0, limit - sentToday)
      : Number(usage.remaining);
  const blocked = !unlimited && (Boolean(usage?.blocked) || remaining <= 0);

  const planLabel = usage?.planName || "Plan";
  const dailyLine = loading
    ? "Loading…"
    : unlimited
      ? `Daily ${sentToday.toLocaleString("en-IN")} · Unlimited`
      : `Daily ${sentToday.toLocaleString("en-IN")} / ${limit.toLocaleString("en-IN")} · ${remaining.toLocaleString("en-IN")} left`;

  return (
    <header
      className={`campaign-report-header daily-campaign-limit-banner${
        blocked ? " is-blocked" : ""
      }`}
      role="status"
      data-testid="daily-campaign-limit-banner"
    >
      <div className="daily-campaign-limit-main">
        <h1>
          {icon} {title}
        </h1>
        <div className="daily-campaign-limit-meta">
          <strong>{loading ? "…" : planLabel}</strong>
          <span>{dailyLine}</span>
        </div>
      </div>
    </header>
  );
};

export default DailyCampaignLimitBanner;
